import { useRouter } from 'next/router'
import React, { useEffect } from 'react'

import { getFilterIntegrations } from '../../../helpers/getFilterIntegrations'
//
import { ButtonNew, FilterOverview, Kpi, Search, SearchInput } from '../../../storybook'
import Hero from '../../../storybook/Hero/Hero'
import { getMicroCopy } from '../../../utils/microcopy'
import { getUrl } from '../../../utils/urls'
import { LocationContext, SiteDataContext } from '../../page-wrapper'
import BlockCrissCross from '../crissCross'
import BlockSectionHeader from '../sectionHeader'

import IntegrationsList from './IntegrationsList'
import { useMicroCopyData } from '@/src/context/MicroCopyContext'
import { useNavigationData } from '@/src/context/NavigationContext'
import { useGlobalData } from '@/pages/_app'

const integrationCountQuery = `
  query {
    _allIntegrationModelsMeta {
      count
    }
  }
`

const integrationsQuery = `
  query integrationsQuery($locale: SiteLocale!, $limit: IntType!, $skip: IntType!) {
    allIntegrationModels(first: $limit, locale: $locale, fallbackLocales: en, orderBy: highlight_DESC, skip: $skip) {
      id
      slug
      title
      categories {
        id
      typeField
        slug
        title
      }
      label
      logo {
        title
        url
        alt
        mimeType
      }
      numberOfUsers
      shortDescription
      highlight
      markets {
        id
        title
        countryIsoCode
        enabledLanguages
      }
    }
  }
`

const Filter = ({ data }) => {
  const router = useRouter()
  const { isTextRtl, lang, market } = React.useContext(SiteDataContext)
  const location = React.useContext(LocationContext)
  const { allMarkets } = useGlobalData()
  const { navigationItems } = useNavigationData()
  const microCopyData = useMicroCopyData()

  const orderedCategories = data?.categories

  // state for search / filtering
  const [integrations, setIntegrations] = React.useState([])
  const currentIntegrationsRef = React.useRef([])

  const [isLoading, setIsLoading] = React.useState(true)
  const [value, setValue] = React.useState('')
  const [results, setResults] = React.useState('')
  const [categories] = React.useState(orderedCategories)

  const [_loadedIntegrations, setLoadedIntegrations] = React.useState(false)
  const [filteredIntegrations, setFilteredIntegrations] = React.useState([])
  const [filters, setFilters] = React.useState([])
  const [categorySelected, setCategorySelected] = React.useState('')
  const [marketSelected, setMarketSelected] = React.useState(market?.isoCode || null)

  const isUserInGlobalEnglishMarket = market.isoCode === 'XX' && lang === 'en'

  const handleSelect = (val, id) => {
    const selectedFilterIndex = filters.findIndex((filter) => filter.id === id)

    const newActiveItem = filters[selectedFilterIndex].items.find((item) => item.value === val)
    if (newActiveItem.countryIsoCode) {
      setMarketSelected(newActiveItem.countryIsoCode)
      router.push(newActiveItem.url)
    }
    if (newActiveItem.categorySlug) {
      setCategorySelected(newActiveItem.categorySlug)
      router.push(newActiveItem.url)
    }
  }

  // Handle Country Selection
  const handleMarketSelection = (marketIsoCode) => {
    setMarketSelected(marketIsoCode)
  }

  const updateFilters = () => {
    // defining filters array
    setIsLoading(true)
    return new Promise(() => {
      setLoadedIntegrations(false)
      let selectedCategoryValue = ''
      let selectedCountryValue = ''
      const filtersArr = [
        {
          id: '0',
          title: getMicroCopy({ key: 'categories', data: microCopyData[lang] }),
          showMoreLabel: getMicroCopy({ key: 'showMore', data: microCopyData[lang] }),
          showLessLabel: getMicroCopy({ key: 'showLess', data: microCopyData[lang] }),
          value: '',
          items: [
            {
              id: '0',
              label: getMicroCopy({ key: 'allIntegrations', data: microCopyData[lang] }),
              url: `/${market.slug}/integrations`,
              active: router.query.slug === 'integrations' || !categorySelected,
              categorySlug: 'integrations',
              value: getMicroCopy({ key: 'allIntegrations', data: microCopyData[lang] })
            },
            ...categories.map((node) => {
              const active = node.slug === categorySelected

              if (active) {
                selectedCategoryValue = node.title
              }

              const selectedMarketHash = marketSelected ? `#${marketSelected}` : ''
              return {
                id: node.id,
                label: node.title,
                url: `/${market.slug}/integrations/${node.slug}${selectedMarketHash}`,
                active,
                categorySlug: node.slug,
                value: node.title
              }
            })
          ]
        },
        {
          id: '1',
          title: getMicroCopy({ key: 'countries', data: microCopyData[lang] }),
          showMoreLabel: getMicroCopy({ key: 'showMore', data: microCopyData[lang] }),
          showLessLabel: getMicroCopy({ key: 'showLess', data: microCopyData[lang] }),
          value: '',
          items: [
            {
              id: '0',
              label: getMicroCopy({ key: 'allCountries', data: microCopyData[lang] }),
              url: `${router.asPath.split('#')[0]}`,
              countryIsoCode: 'ALL',
              active: !marketSelected,
              value: '0',
              handleClick: () => setMarketSelected(null)
            },
            ...allMarkets.map((m) => {
              const active = m.countryIsoCode === marketSelected

              if (active) {
                selectedCountryValue = m.countryIsoCode
              }

              return {
                id: m.countryIsoCode,
                label: getMicroCopy({
                  key: `market.${m.countryIsoCode.toLocaleLowerCase()}`,
                  data: microCopyData[lang]
                }),
                value: m.countryIsoCode,
                handleClick: () => handleMarketSelection(m.countryIsoCode),
                url: `#${m.countryIsoCode}`,
                countryIsoCode: m.countryIsoCode,
                active
              }
            })
          ]
        }
      ]

      filtersArr[0].value = selectedCategoryValue
      filtersArr[1].value = selectedCountryValue || '0'

      setFilters(filtersArr)

      if (integrations) {
        // filtering integrations
        let filtered = null
        filtered = integrations
          .filter((node) => {
            // filtering out integrations that are not part of selected category
            const isAllCategoriesSelected =
              !categorySelected ||
              (typeof router.query.slug === 'string' && router.query.slug === 'integrations') ||
              (Array.isArray(router.query.slug) &&
                router.query.slug[0] === 'integrations' &&
                router.query.slug.length === 1)

            if (isAllCategoriesSelected) {
              return true
            }

            const arrCategories =
              node.categories &&
              Array.isArray(node.categories) &&
              node.categories.map((cat) => cat.slug)

            return arrCategories.includes(categorySelected)
          })
          .filter((node) => {
            // filtering out integrations that are not part of selected market (country)
            if (!marketSelected) {
              return true
            }

            const arrCountries =
              node.markets &&
              Array.isArray(node.markets) &&
              node.markets.map((market) => market?.countryIsoCode)

            return arrCountries?.includes(marketSelected)
          })
          .map((node) => {
            return {
              ...node,
              slug:
                node.markets.find((m) => m?.countryIsoCode == market.isoCode) ||
                isUserInGlobalEnglishMarket
                  ? node.slug
                  : null
            }
          })

        setFilteredIntegrations(filtered)
        setLoadedIntegrations(true)
        setIsLoading(false)
      } else {
        setLoadedIntegrations(true)
        setIsLoading(false)
      }
    })
  }

  // Fetching all integrations on load

  useEffect(() => {
    const fetchIntegrations = async () => {
      let allIntegrations = []
      const countRes = await fetch('/api/dato-fetch', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ query: integrationCountQuery })
      })
      const countData = await countRes.json()
      const limit = 300
      const totalPages = Math.ceil(countData._allIntegrationModelsMeta.count / limit)

      for (let i = 0; i < totalPages; i++) {
        const res = await fetch('/api/dato-fetch', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            query: integrationsQuery,
            variables: { locale: lang, limit: limit, skip: i * limit }
          })
        })

        const data = await res.json()
        allIntegrations = [...allIntegrations, ...data?.allIntegrationModels]
      }

      setIntegrations(allIntegrations || [])
      currentIntegrationsRef.current = allIntegrations

      setResults(
        allIntegrations.map((node) => {
          const { id, logo, slug, title, markets } = node
          const isEnabledInMarket =
            !!markets.find((m) => m?.countryIsoCode == market.isoCode) ||
            isUserInGlobalEnglishMarket
          return {
            label: title,
            id: id,
            url:
              slug && isEnabledInMarket ? `/${data.extra.slugPrefix}/integrations/${slug}` : null,
            logo: logo || ''
          }
        })
      )
    }
    fetchIntegrations()
  }, [])

  useEffect(() => {
    if (location.asPath.split('#')[1]) {
      setMarketSelected(location.asPath.split('#')[1])
    }
    if (router.query.slug) {
      if (typeof router.query.slug === 'string') {
        setCategorySelected(router.query.slug !== 'integrations' ? router.query.slug : '')
      } else if (Array.isArray(router.query.slug)) {
        // If it's an array, we'll use the last element
        const lastSlug = router.query.slug[router.query.slug.length - 1]
        setCategorySelected(lastSlug !== 'integrations' ? lastSlug : '')
      }
    } else {
      setCategorySelected('')
    }
  }, [location.asPath])

  // Fetching all filters
  useEffect(() => {
    if (Object.keys(microCopyData).length > 0 && integrations.length > 0) {
      updateFilters()
    }
  }, [categorySelected, marketSelected, integrations])

  useEffect(() => {
    if (location.hash && location.hash !== '#') {
      const autoSelectedCountry = market?.isoCode?.toUpperCase()
      setMarketSelected(autoSelectedCountry)
      location.hash = '#' + autoSelectedCountry
      router.push(location.hash)
    }
  }, [])

  const searchIntegration = React.useCallback((val) => {
    const result = currentIntegrationsRef.current.filter((node) => {
      const { title } = node
      const currentTitle = (title || '').toLowerCase()
      return currentTitle.includes((val || '').toLowerCase())
    })

    // Sorting results...
    result.sort((a, b) => {
      const titleA = a?.title.toLowerCase()
      const titleB = b?.title.toLowerCase()
      if (titleA.startsWith(val.toLowerCase()) && titleB.startsWith(val.toLowerCase())) {
        return 0
      } else {
        if (titleA.startsWith(val.toLowerCase())) {
          return -1
        }
        if (titleB.startsWith(val.toLowerCase())) {
          return 1
        }
      }
      return 0
    })

    return result
  }, [])

  const updateValue = (val) => {
    // get list of integrations that match typed string
    const resultArr = searchIntegration(val)

    // update state with new string and results
    setValue(val)
    setResults(
      resultArr.map((node) => {
        const { id, logo, slug, title, markets } = node
        const isEnabledInMarket =
          !!markets.find((m) => m?.countryIsoCode == market.isoCode) || isUserInGlobalEnglishMarket
        return {
          label: title,
          id: id,
          url: slug && isEnabledInMarket ? `/${data.extra.slugPrefix}/integrations/${slug}` : null,
          logo: logo || ''
        }
      })
    )
  }

  // Get all integrations that have a 'comingSoon' or 'voteNow' label
  const activeIntegrations =
    filteredIntegrations.filter(
      (node) => node.label !== 'comingSoon' && node.label !== 'voteNow'
    ) || []

  const notActiveIntegrations =
    filteredIntegrations && Array.isArray(filteredIntegrations)
      ? filteredIntegrations.filter(
          (node) => node.label === 'comingSoon' || node.label === 'voteNow'
        )
      : []

  const slug = categorySelected
  const getIntegrationsWithSlug = getFilterIntegrations(categories, slug, 'integrations')

  const heroTitle = getIntegrationsWithSlug('pageTitle', data?.title)
  const heroContent = getIntegrationsWithSlug('introCopy', data?.description)
  const bodyContent = getIntegrationsWithSlug('bodyCopy')
  const bodyTitle = getIntegrationsWithSlug('bodyTitle') || ''
  const crissCrosses = getIntegrationsWithSlug('crissCrosses')
  const titleCrissCrosses = getIntegrationsWithSlug('titleCrissCrosses')
  const variant = bodyContent ? 'small' : ''

  return (
    <>
      <Hero title={bodyTitle || heroTitle} content={heroContent} background={'l-section'}>
        <Search
          value={value}
          results={results}
          emptyState={
            <Kpi
              align={'center'}
              {...{
                title: data?.smallBlock?.title || '',
                description: data.smallBlock?.description || ''
              }}
            >
              <ButtonNew
                size={'md'}
                variant={'tertiary'}
                icon={'ti-arrow-narrow-right'}
                url={`/${data.extra.slugPrefix}/${data.smallBlock?.action?.content?.[0]?.slug}`}
              >
                {data?.smallBlock?.action?.title || ''}
              </ButtonNew>
            </Kpi>
          }
        >
          <SearchInput
            placeholder={getMicroCopy({
              key: 'searchInputPlaceholder',
              data: microCopyData[lang]
            })}
            searchLabel={getMicroCopy({
              key: 'searchInputLabel',
              data: microCopyData[lang]
            })}
            handleSearch={updateValue}
          />
        </Search>
      </Hero>
      <section
        dir={isTextRtl ? 'rtl' : 'ltr'}
        data-variant={variant}
        className="l-section filter-overview"
      >
        <div className="l-container:12/12 l-container--default-spacing">
          <div className="l-grid l-grid--default-spacing">
            <div className="l-grid__col:3/12 l-grid__col:12/12@to:viewport-7 l-grid__col:4/12@to:viewport-9">
              <FilterOverview filters={filters} handleSelect={handleSelect} />
            </div>

            <div className="l-grid__col:9/12 l-grid__col:12/12@to:viewport-7 l-grid__col:8/12@to:viewport-9">
              {!categorySelected && (
                <h2
                  style={{
                    fontFamily: '"Proxima Nova", "Arial", sans-serif',
                    marginBottom: '1.75rem',
                    fontWeight: 700,
                    fontSize: '1.125rem'
                  }}
                >
                  {getMicroCopy({
                    key: 'activeIntegrations',
                    data: microCopyData[lang]
                  })}
                </h2>
              )}

              {isLoading ? (
                <div>
                  <IntegrationsList
                    integrations={new Array(6).fill(1)}
                    skeleton={true}
                    limit={46}
                    labels={{
                      paginationLoadMore: getMicroCopy({
                        key: 'paginationLoadMore',
                        data: microCopyData[lang]
                      }),
                      paginationOf: getMicroCopy({
                        key: 'paginationOf',
                        data: microCopyData[lang]
                      })
                    }}
                  />
                </div>
              ) : (
                <div>
                  {filteredIntegrations.length > 0 && isLoading === false ? (
                    <IntegrationsList
                      integrations={activeIntegrations}
                      language={lang}
                      slugPrefix={data.extra.slugPrefix}
                      labels={{
                        paginationLoadMore: getMicroCopy({
                          key: 'paginationLoadMore',
                          data: microCopyData[lang]
                        }),
                        paginationOf: getMicroCopy({
                          key: 'paginationOf',
                          data: microCopyData[lang]
                        })
                      }}
                    />
                  ) : (
                    <p>
                      {getMicroCopy({
                        key: 'noResultsMessage',
                        data: microCopyData[lang]
                      })}
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {!categorySelected && (
        <>
          <div className="l-container:12/12 l-container--default-spacing">
            <hr />
          </div>

          <section className="l-section" dir={isTextRtl ? 'rtl' : 'ltr'}>
            <div className="l-container:12/12 l-container--default-spacing">
              <div className="l-grid l-grid--default-spacing">
                <div className="l-grid__col:3/12 l-grid__col:12/12@to:viewport-7 l-grid__col:4/12@to:viewport-9">
                  <Kpi
                    {...{
                      title: data?.smallBlock?.title || '',
                      description: data.smallBlock?.description || ''
                    }}
                  >
                    {data.smallBlock?.action ? (
                      <ButtonNew
                        size={'md'}
                        variant={'tertiary'}
                        icon={'ti-arrow-narrow-right'}
                        url={getUrl({
                          navItem: data.smallBlock?.action,
                          slugPrefix: data.extra.slugPrefix,
                          navigationItems: navigationItems
                        })}
                      >
                        {data.smallBlock?.action?.title || ''}
                      </ButtonNew>
                    ) : null}
                  </Kpi>
                </div>

                <div className="l-grid__col:9/12 l-grid__col:12/12@to:viewport-7 l-grid__col:8/12@to:viewport-9">
                  <h5 style={{ marginBottom: '1.75rem' }}>
                    {getMicroCopy({
                      key: 'soonOnDeliverect',
                      data: microCopyData[lang]
                    })}
                  </h5>

                  {notActiveIntegrations.length > 0 ? (
                    <IntegrationsList
                      integrations={notActiveIntegrations}
                      slugPrefix={data.extra.slugPrefix}
                      labels={{
                        paginationLoadMore: getMicroCopy({
                          key: 'paginationLoadMore',
                          data: microCopyData[lang]
                        }),
                        paginationOf: getMicroCopy({
                          key: 'paginationOf',
                          data: microCopyData[lang]
                        })
                      }}
                    />
                  ) : (
                    <p>
                      {getMicroCopy({
                        key: 'noResultsMessage',
                        data: microCopyData[lang]
                      })}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </section>
        </>
      )}

      {bodyContent && <Hero content={bodyContent} title={bodyTitle} small textContent />}

      {titleCrissCrosses && <BlockSectionHeader data={titleCrissCrosses} />}

      {crissCrosses
        ? crissCrosses.map((crissCross) => (
            <BlockCrissCross key={crissCross.id} data={crissCross} />
          ))
        : null}
    </>
  )
}

export default Filter
